import React, { useRef, useState } from "react";
// Components
import { RoosterGetEditorRefPlugin } from "../richTextEditor/plugins/RoosterGetEditorRefPlugin";
import { ChatMessageEditor } from "./ChatMessageEditor";
import { bundleIcon, TextEditStyleFilled, TextEditStyleRegular, SendFilled, SendRegular } from "@fluentui/react-icons";
import { Button, ToggleButton, Divider } from "@fluentui/react-components";
import { RichTextEditorRibbon } from "../richTextEditor/RichTextEditorRibbon";
import { useChatComposeSectionStyles } from "./ChatComposeSection.styles";
import { createRibbonPlugin } from "roosterjs-react/lib-mjs";
// Constants
import { KnownRibbonButtonKey } from "roosterjs-react/lib-mjs/ribbon/type/KnownRibbonButton";
import { AllowedChatDataAttributesForSerialize } from "@ms/uno-constants/lib/local/ChatConstants";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { sanitizeContent } from "@ms/uno-utilities/lib/local/ContentSanitizationUtilities";
import { hasNonWhitespaceContent } from "@ms/uno-utilities/lib/local/HtmlUtilities";
import { serializeEmbeddedHtmlMentions } from "../../../utilities/ChatAtMentionsUtilities";
import { fontSizeButton } from "./ribbonButtons/FontSizeButton";
import { backgroundColorButton } from "./ribbonButtons/BackgroundColorButton";
import { RoosterContentLimitPlugin } from "../richTextEditor/plugins/RoosterContentLimitPlugin";
const TextEditStyle = bundleIcon(TextEditStyleFilled, TextEditStyleRegular);
const Send = bundleIcon(SendFilled, SendRegular);
const maxLength = 65536; // 2^16 Bytes = 64KiB. IC3 supports more, but that is for the entire message payload, including metadata.
const ribbonButtonKeys = [
    KnownRibbonButtonKey.Bold,
    KnownRibbonButtonKey.Italic,
    KnownRibbonButtonKey.Underline,
    KnownRibbonButtonKey.Strikethrough,
    KnownRibbonButtonKey.BulletedList,
    KnownRibbonButtonKey.NumberedList,
    KnownRibbonButtonKey.BackgroundColor,
    KnownRibbonButtonKey.TextColor,
    KnownRibbonButtonKey.FontSize,
    KnownRibbonButtonKey.Code,
    KnownRibbonButtonKey.Quote,
    KnownRibbonButtonKey.InsertLink,
    KnownRibbonButtonKey.ClearFormat
];
/**
 * Section in chat for composing messages
 */ export function ChatComposeSection({ callbacks, recentMessages, prioritizedUserIdsForMentionSuggestions, focusOnInit }) {
    // Refs
    const pluginsInitialized = useRef(false);
    const getEditorRefPlugin = useRef();
    const ribbonPlugin = useRef(null);
    const plugins = useRef([]);
    const editorContainerRef = useRef(null);
    // State
    const [isEditorRibbonShown, setIsEditorRibbonShown] = useState(false);
    // Plugins
    if (!pluginsInitialized.current) {
        getEditorRefPlugin.current = new RoosterGetEditorRefPlugin();
        ribbonPlugin.current = createRibbonPlugin();
        plugins.current.push(new RoosterContentLimitPlugin(maxLength));
        plugins.current.push(getEditorRefPlugin.current);
        plugins.current.push(ribbonPlugin.current);
        pluginsInitialized.current = true;
    }
    // Callbacks
    const onClickSend = ()=>{
        const editor = getEditorRefPlugin.current?.getEditorRef();
        if (!editor) {
            return;
        }
        const content = editor.getContent();
        if (!hasNonWhitespaceContent(content)) {
            return;
        }
        // Before send message, parse any at-mention-elements into format for storage
        const { messageContent, mentions } = serializeEmbeddedHtmlMentions(content);
        const sanitizedMessageContent = sanitizeContent(messageContent, {
            allowedAttributes: AllowedChatDataAttributesForSerialize
        });
        callbacks.onSendMessage({
            content: sanitizedMessageContent,
            mentions
        });
        editor.setContent("");
        editor.focus();
    };
    const toggleFormattingRibbon = ()=>{
        setIsEditorRibbonShown((pre)=>!pre);
    };
    const renderRibbon = ()=>{
        if (!isEditorRibbonShown || !ribbonPlugin.current) {
            return;
        }
        return /*#__PURE__*/ React.createElement(RichTextEditorRibbon, {
            plugin: ribbonPlugin.current,
            buttonKeys: ribbonButtonKeys,
            customizedButtons: [
                fontSizeButton,
                backgroundColorButton
            ]
        });
    };
    const toggleFormattingButtonLabel = isEditorRibbonShown ? Strings.TaskChat_HideFormattingOptions : Strings.TaskChat_ShowFormattingOptions;
    const styles = useChatComposeSectionStyles();
    return /*#__PURE__*/ React.createElement("div", {
        className: styles.root
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.ribbonContainer
    }, renderRibbon()), /*#__PURE__*/ React.createElement("div", {
        className: styles.editorContainer,
        ref: editorContainerRef
    }, /*#__PURE__*/ React.createElement(ChatMessageEditor, {
        plugins: plugins.current,
        getMentionableUsers: callbacks.getMentionableUsers,
        onOpenAtMentionPicker: callbacks.onOpenAtMentionPicker,
        getUserPhoto: callbacks.getUserPhoto,
        onRenderPersona: callbacks.onRenderPersona,
        isEditorRibbonShown: isEditorRibbonShown,
        onSendMessage: onClickSend,
        toggleFormattingRibbon: toggleFormattingRibbon,
        recentMessages: recentMessages,
        prioritizedUserIdsForMentionSuggestions: prioritizedUserIdsForMentionSuggestions,
        atMentionPickerMountNode: editorContainerRef.current,
        focusOnInit: focusOnInit
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.buttonsContainer
    }, /*#__PURE__*/ React.createElement(ToggleButton, {
        checked: isEditorRibbonShown,
        icon: /*#__PURE__*/ React.createElement(TextEditStyle, null),
        onClick: toggleFormattingRibbon,
        appearance: "transparent",
        title: toggleFormattingButtonLabel,
        "aria-label": toggleFormattingButtonLabel
    }), /*#__PURE__*/ React.createElement(Divider, {
        vertical: true,
        className: styles.divider
    }), /*#__PURE__*/ React.createElement(Button, {
        icon: /*#__PURE__*/ React.createElement(Send, null),
        onClick: onClickSend,
        appearance: "transparent",
        "data-testid": "chatSendButton",
        title: Strings.TaskComments_SendCommentButtonLabel,
        "aria-label": Strings.TaskComments_SendCommentButtonLabel
    })));
}
